import { SetterOrUpdater } from "recoil"
import { FilterDef } from "../constants/FinanceModels"
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import React from "react";

/**
 * Adds the key and value into the FilterDef array using provided RecoilState reference.
 * If any key was previously added into FilterDef array, it will be overriden.
 *
 * @param key the key of the filter.
 * @param value the value of the filter.
 * @param display the displayable value of the filter, by default uses value parameter.
 * @param recoilSetterOrUpdater the SetterOrUpdater reference to use.
 */
export function addFilter(recoilSetterOrUpdater: SetterOrUpdater<FilterDef[]>, key: string, value: any, display: string = value) {
    if (key === undefined || key === null) return;

    recoilSetterOrUpdater((old) => old.filter(it => it.key !== key));
    if (value !== null && ((!Array.isArray(value) && value !== "") || (Array.isArray(value) && value.length > 0))) {
        recoilSetterOrUpdater((old) => [
            ...old,
            {
                key: key,
                value: value,
                display: display,
                onDelete: () => delFilter(recoilSetterOrUpdater, key)
            }
        ])
    }
}

/**
 * Removes the key from the FilterDef array using provided RecoilState reference.
 *
 * @param key the key of the filter.
 * @param recoilSetterOrUpdater the SetterOrUpdater reference to use.
 */
export function delFilter(recoilSetterOrUpdater: SetterOrUpdater<FilterDef[]>, key: string) {
    recoilSetterOrUpdater((old) => old.filter(it => it.key !== key));
}

/**
 * Gets the value of the FilterDef identified by the provided key.
 *
 * @param key the key of the filter to look for
 * @param filterArray the FilterDef array to look into
 * @returns the value of the filter associated with the provided key if any.
 */
export function getFilterValue<T>(key: string, filterArray: FilterDef[]): T {
    return filterArray.find(it => it.key === key)?.value;
}

/**
 * Compares two objects to check if they are equal.
 *
 * @param obj1 The first object to compare.
 * @param obj2 The second object to compare.
 * @returns True if the objects are equal, otherwise false.
 */
export const areObjectsEqual = (obj1: any, obj2: any): boolean => {

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }
   
    for (let key of keys1) {
      if (
        Array.isArray(obj1[key]) &&
        Array.isArray(obj2[key]) &&
        obj1[key].length === 0 &&
        obj2[key].length === 0
      ) {
        continue; 
      }
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }
    return true;
  };

export const DECIMAL_REG_EXP =  /^\s*-?[0-9]\d*(\.\d{0,2})?\s*$/

export const renderMultiSelect = (label: string, list: any[], name: string, isHidden: boolean, onChange?: any) => {
  return (field: any, error: any, formState: any, watchAll: any, setValue: any) => (
    <TextField
      name={name}
      fullWidth
      select
      hidden={isHidden}
      disabled={list.length==0}
      SelectProps={{
        multiple: true,
        renderValue: (selected: any) =>
          selected
            .map((s: any) => list.find((ss) => ss.value === s)!.label)
            .join(", "),
      }}
      label={label}
      value={field.value}
      onChange={(event) => {
        if (onChange) {
          onChange(event.target.value, setValue);
        }
        field.onChange(event);
      }}
      error={error !== undefined}
      helperText={error ? error.message : ""}
    >
      {list.map((item) => (
        <MenuItem key={item.value} value={item.value}>
          <Checkbox checked={field.value?.indexOf(item.value) > -1} />
          <ListItemText primary={item.label} />
        </MenuItem>
      ))}
    </TextField>
  );
};

export const renderSelect =
    (name: string, label: string, items: any[]) => (field: any) =>
        (
            <TextField
                name={name}
                fullWidth
                select
                label={label}
                value={field.value}
                onChange={field.onChange}
            >
                {items.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </TextField>
        );
